import { TargetingLocationType, type GeolocationOption, type TargetingLocation } from '../../model';
import type { GeolocationTargetingClient } from '../../api';

export type TargetingGeolocationsClient = GeolocationTargetingClient;

export type GeoNode = {
  id: string;
  label: string;
  option: GeolocationOption;
  type: TargetingLocationType;
  children?: GeoNode[] | undefined;
  loading?: boolean | undefined;
};

export function formatEnumLabel(value: string | undefined) {
  if (!value) {
    return '-';
  }

  return value
    .toLowerCase()
    .split('_')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

export function toGeoNode(option: GeolocationOption, type: TargetingLocationType): GeoNode {
  return {
    id: geoOptionKey(option, type),
    label: geoOptionLabel(option, type),
    option,
    type,
  };
}

export function geoSearchResultType(option: GeolocationOption): TargetingLocationType {
  if (option.cityId || option.cityName) return TargetingLocationType.City;
  if (option.subdivision2IsoCode) return TargetingLocationType.Subregion;
  if (option.subdivision1IsoCode) return TargetingLocationType.Region;
  if (option.countryIsoCode) return TargetingLocationType.Country;
  return TargetingLocationType.Continent;
}

export function formatGeoTypeLabel(type: TargetingLocationType | string) {
  return formatEnumLabel(type);
}

export function geoLocationPrimaryLabel(location: TargetingLocation) {
  return location.locationName ?? location.countryName ?? formatGeoTypeLabel(location.type);
}

export function geoLocationMetaLabel(location: TargetingLocation) {
  const metaParts = [
    formatGeoTypeLabel(location.type),
    location.countryName,
    location.subdivision1Name,
    location.subdivision2Name,
  ];
  const uniqueParts = metaParts.filter(
    (part, index): part is string =>
      Boolean(part) &&
      part !== geoLocationPrimaryLabel(location) &&
      metaParts.indexOf(part) === index,
  );

  return uniqueParts.length ? uniqueParts.join(', ') : formatGeoTypeLabel(location.type);
}

export function geoLocationKey(location: TargetingLocation) {
  return [
    location.type,
    location.continentId,
    location.countryId,
    location.regionId,
    location.subRegionId,
    location.geocodeId,
    location.locationName,
  ]
    .filter(Boolean)
    .join(':');
}

export function toTargetingLocation(node: GeoNode, excluded: boolean): TargetingLocation {
  const option = node.option;
  return {
    continentId: option.continentCode,
    countryId: option.countryIsoCode,
    countryName: option.countryName,
    excluded,
    type: node.type,
    geocodeId: option.cityId ?? option.geonameId,
    regionId: option.subdivision1IsoCode,
    subRegionId: option.subdivision2IsoCode,
    subdivision1Name: option.subdivision1Name,
    subdivision2Name: option.subdivision2Name,
    locationName: geoOptionLabel(option, node.type),
    tag: node.type,
  };
}

export function applyGeoNodePatches(
  node: GeoNode,
  patches: Record<string, Partial<GeoNode>>,
): GeoNode {
  const patch = patches[node.id];
  const patchedNode = patch ? { ...node, ...patch } : node;
  if (!patchedNode.children) {
    return patchedNode;
  }

  return {
    ...patchedNode,
    children: patchedNode.children.map((child) => applyGeoNodePatches(child, patches)),
  };
}

export async function loadGeoChildren(client: TargetingGeolocationsClient, node: GeoNode) {
  if (node.type === TargetingLocationType.Continent) {
    const countries = await client.listCountries({
      continent: node.option.continentCode ?? '',
    });
    return countries.map((option) => toGeoNode(option, TargetingLocationType.Country));
  }
  if (node.type === TargetingLocationType.Country) {
    const regions = await client.listRegions({
      country: node.option.countryIsoCode ?? '',
    });
    return regions.map((option) => toGeoNode(option, TargetingLocationType.Region));
  }
  if (node.type === TargetingLocationType.Region) {
    const subregions = await client.listSubregions({
      country: node.option.countryIsoCode ?? '',
      region: node.option.subdivision1IsoCode ?? '',
    });
    if (subregions.length) {
      return subregions.map((option) => toGeoNode(option, TargetingLocationType.Subregion));
    }
    const cities = await client.listCities({
      country: node.option.countryIsoCode ?? '',
      region: node.option.subdivision1IsoCode ?? '',
    });
    return cities.map((option) => toGeoNode(option, TargetingLocationType.City));
  }
  if (node.type === TargetingLocationType.Subregion) {
    const cities = await client.listCities({
      country: node.option.countryIsoCode ?? '',
      region: node.option.subdivision1IsoCode ?? '',
      subregion: node.option.subdivision2IsoCode ?? '',
    });
    return cities.map((option) => toGeoNode(option, TargetingLocationType.City));
  }

  return [];
}

function geoOptionKey(option: GeolocationOption, type: TargetingLocationType) {
  const keyParts = [
    type,
    option.continentCode,
    option.countryIsoCode,
    option.subdivision1IsoCode,
    option.subdivision2IsoCode,
    option.cityId ?? option.geonameId,
    geoOptionLabel(option, type),
  ];

  return keyParts.filter(Boolean).join(':');
}

function geoOptionLabel(option: GeolocationOption, type: TargetingLocationType) {
  switch (type) {
    case TargetingLocationType.Continent:
      return option.continentName ?? option.name;
    case TargetingLocationType.Country:
      return option.countryName ?? option.name;
    case TargetingLocationType.Region:
      return option.subdivision1Name ?? option.name;
    case TargetingLocationType.Subregion:
      return option.subdivision2Name ?? option.name;
    case TargetingLocationType.City:
      return option.cityName ?? option.name;
    case TargetingLocationType.Zip:
    default:
      return option.name;
  }
}
